import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AuthPhoneField from "../../../customComponents/AuthPhoneField";
import EmailInputWithDomainSuggest from "../../../customComponents/EmailInputWithDomainSuggest";
import { startAuthentication } from "@simplewebauthn/browser";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { ProfileContext } from "../../../context/ProfileProvider";
import {
  getPasswordRuleMeta,
  getPasswordRuleRowStates,
  validatePasswordAgainstRules,
} from "../../../utils/authPasswordRules";
import { countriesList } from "../../../utils/CountriesList";
import "../PasskeyPage/passkey.css";
import "./changeLoginPassword.css";
import "../WithdrawPage/withdrawal.css";

const PHASE = {
  RESTRICT: "restrict",
  VERIFY_LOGIN: "verify_login",
  CHANGE: "change",
  RESET_TABS: "reset_tabs",
  VERIFY_FORGOT: "verify_forgot",
  RESET_NEW: "reset_new",
};

/** Best-effort split of stored mobile into dial code + national digits for `AuthPhoneField`. */
function splitInternationalMobile(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return { countryCode: "+91", phoneDigits: "" };
  const digitsOnly = s.replace(/\D/g, "");
  const sorted = [...countriesList].sort(
    (a, b) => String(b.value).replace(/\D/g, "").length - String(a.value).replace(/\D/g, "").length
  );
  for (const c of sorted) {
    const dialDigits = String(c.value).replace(/\D/g, "");
    if (dialDigits && digitsOnly.startsWith(dialDigits)) {
      return {
        countryCode: c.value,
        phoneDigits: digitsOnly.slice(dialDigits.length),
      };
    }
  }
  return { countryCode: "+91", phoneDigits: digitsOnly };
}

function useOtpHandlers(otp, setOtp) {
  const handleChange = (idx, e) => {
    const digit = String(e.target.value ?? "").replace(/\D/g, "").slice(-1);
    setOtp((prev) => {
      const next = [...prev];
      next[idx] = digit;
      return next;
    });
    if (digit && idx < 5) {
      window.requestAnimationFrame(() => {
        document.getElementById(`pm-otp-${idx + 1}`)?.focus();
      });
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      window.requestAnimationFrame(() => {
        document.getElementById(`pm-otp-${idx - 1}`)?.focus();
      });
    }
  };

  const handleInputPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");
    const digits = String(pasteData || "").replace(/\D/g, "").slice(0, 6).split("");
    if (digits.length > 0) {
      setOtp((prev) => {
        const next = [...prev];
        digits.forEach((d, i) => {
          if (i < 6) next[i] = d;
        });
        return next;
      });
      window.requestAnimationFrame(() => {
        const targetIdx = digits.length === 6 ? 5 : digits.length;
        document.getElementById(`pm-otp-${targetIdx}`)?.focus();
      });
    }
  };

  const isComplete = otp.every((d) => d && d.length === 1);
  return { handleChange, handleKeyDown, handleInputPaste, isComplete };
}

const OtpModal = ({
  title,
  subtitle,
  otp,
  setOtp,
  onClose,
  onConfirm,
  footerExtra,
  onResend,
  resendDisabled,
  confirmDisabled,
  confirmLabel,
}) => {
  const { handleChange, handleKeyDown, handleInputPaste, isComplete } = useOtpHandlers(otp, setOtp);

  const handlePaste = async () => {
    try {
      const raw = await navigator.clipboard.readText();
      const digits = String(raw || "").replace(/\D/g, "").slice(0, 6).split("");
      if (digits.length !== 6) return;
      setOtp(digits);
      window.requestAnimationFrame(() => {
        document.getElementById("pm-otp-5")?.focus();
      });
    } catch {
      // ignore
    }
  };

  return (
    <div className="passkey-modal-overlay" role="presentation">
      <div
        className="passkey-modal passkey-verify-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pm-verify-title"
      >
        <button type="button" className="passkey-modal__close" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <h2 id="pm-verify-title" className="passkey-verify-modal__title">
          {title}
        </h2>
        <p className="passkey-verify-modal__subtitle">{subtitle}</p>
        <div className="passkey-verify-modal__otpRow" aria-label="Verification code">
          {otp.map((d, idx) => (
            <input
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              id={`pm-otp-${idx}`}
              className="passkey-verify-modal__otp"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              autoComplete={idx === 0 ? "one-time-code" : undefined}
              aria-label={`Digit ${idx + 1}`}
              value={d}
              onChange={(e) => handleChange(idx, e)}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              onPaste={handleInputPaste}
            />
          ))}
        </div>
        <div className="passkey-verify-modal__links">
          <button
            type="button"
            className="passkey-verify-modal__link"
            disabled={Boolean(resendDisabled) || !onResend}
            onClick={() => onResend?.()}
          >
            Resend
          </button>
          <button
            type="button"
            className="passkey-verify-modal__link passkey-verify-modal__link--paste"
            onClick={handlePaste}
          >
            Paste <i className="ri-file-copy-fill" aria-hidden="true" />
          </button>
        </div>
        <button
          type="button"
          className="passkey-verify-modal__confirm"
          disabled={!isComplete || Boolean(confirmDisabled)}
          onClick={onConfirm}
        >
          {confirmLabel || "Confirm"}
        </button>
        {footerExtra ? <div className="pm-verifyFooter">{footerExtra}</div> : null}
      </div>
    </div>
  );
};

const AuthenticatorAppModal = ({
  code,
  setCode,
  onClose,
  onSubmit,
  busy,
  onSwitchMethod,
  onSecurityUnavailable,
}) => {
  const handlePaste = async () => {
    try {
      const raw = await navigator.clipboard.readText();
      const digits = String(raw || "").replace(/\D/g, "").slice(0, 6);
      if (digits.length !== 6) return;
      setCode(digits);
    } catch {
      // ignore
    }
  };

  return (
    <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation" onClick={(e) => e.stopPropagation()}>
      <div className="withdraw_auth_modal" role="dialog" aria-modal="true" aria-label="Authenticator App Verification">
        <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <div className="withdraw_auth_title">Authenticator App Verification</div>
        <div className="withdraw_auth_subtitle">
          Enter the 6-digit code generated by the
          <br />
          Authenticator App.
        </div>
        <div className="withdraw_auth_label">Authenticator App</div>
        <div className="withdraw_auth_input_row">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder=""
            value={code}
            onChange={(e) => setCode(String(e.target.value || "").replace(/\D/g, "").slice(0, 6))}
          />
          <button type="button" className="withdraw_auth_paste" onClick={handlePaste}>
            Paste
          </button>
        </div>
        <button type="button" className="withdraw_auth_submit" disabled={busy} onClick={onSubmit}>
          {busy ? "Verifying…" : "Submit"}
        </button>
        <div className="gapbtn_space">
          <button type="button" className="withdraw_auth_link" onClick={() => onSwitchMethod?.()}>
            Switch to Another Verification Method
          </button>
          <button type="button" className="withdraw_auth_link" onClick={() => onSecurityUnavailable?.()}>
            Security verification unavailable?
          </button>
        </div>
        <div className="withdraw_passkey_footer withdraw_auth_footer">
          <img src="/images/protected_icon.svg" alt="shield icon" />
          <span>Protected by Balance Risk</span>
        </div>
      </div>
    </div>
  );
};

const CHOOSE_METHOD_ROW = {
  passkey: {
    title: "Passkey",
    description: "Sign in with Face ID, Touch ID, or your security key.",
    icon: "/images/passkey_vector.svg",
  },
  email: {
    title: "Email OTP",
    description: "Get a one-time code sent to your email",
    icon: "/images/email_vector2.svg",
  },
  mobile: {
    title: "Mobile OTP",
    description: "Receive a verification code via SMS",
    icon: "/images/mobileicon.svg",
  },
  totp: {
    title: "Authenticator App",
    description: "Enter the 6-digit code from your authenticator app",
    icon: "/images/authenticator_icon.svg",
  },
};

const ChooseAuthenticatorModal = ({
  methods,
  onClose,
  onPick,
  onSecurityUnavailable,
}) => {
  return (
    <div
      className="withdraw_confirm_overlay pm-choose-method-overlay-stacked"
      role="presentation"
    >
      <div
        className="withdraw_choose_modal pm-choose-method-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pm-choose-verify-title"
      >
        <div className="withdraw_choose_topbar pm-choose-method-modal__topbar">
          <span className="pm-choose-method-modal__topbarSpacer" aria-hidden="true" />
          <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <h2 id="pm-choose-verify-title" className="withdraw_choose_title pm-choose-method-modal__title">
          Choose Verification Method
        </h2>
        <p className="withdraw_choose_subtitle pm-choose-method-modal__subtitle">
          Select how you&apos;d like to confirm your identity
        </p>

        <div className="emailphone_outerbl pm-choose-method-modal__list">
          {methods.map((key) => {
            const row = CHOOSE_METHOD_ROW[key];
            if (!row) return null;
            return (
              <button
                key={key}
                type="button"
                className="withdraw_choose_item pm-choose-method-item"
                onClick={() => onPick(key)}
              >
                <img src={row.icon} alt="" aria-hidden="true" />
                <div className="pm-choose-method-item__text">
                  <span className="pm-choose-method-item__title">{row.title}</span>
                  <span className="pm-choose-method-item__desc">{row.description}</span>
                </div>
                <i className="ri-arrow-right-s-line pm-choose-method-item__chevron" aria-hidden="true" />
              </button>
            );
          })}
        </div>

        <div className="mid_use_text">
          <button type="button" className="withdraw_auth_link" onClick={() => onSecurityUnavailable?.()}>
            Security verification unavailable?
          </button>
        </div>

        <div className="withdraw_passkey_footer withdraw_auth_footer passkey_footer_text">
          <div className="withdraw_passkey_footer__inner">
            <img src="/images/protected_icon.svg" alt="shield icon" />
            <span>Protected by Balance Risk</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Password Management flow — Change login password + forgot reset.
 * Route: /user_profile/security/changeLoginPassword
 */
const ChangeLoginPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isFund = searchParams.get("type") === "fund";

  const initialVerifyMethod = location?.state?.passwordVerifyMethod; // "passkey" | "totp" | "email" | "mobile" | undefined
  /** Merged from navigation + optional refresh from API when opening “Switch method”. */
  const [resolvedSecurityMethods, setResolvedSecurityMethods] = useState(
    () => location?.state?.availableSecurityMethods ?? null
  ); // { passkey, totp, email, mobile }
  const [verifyMethod, setVerifyMethod] = useState(initialVerifyMethod);
  const [passkeyUserId, setPasskeyUserId] = useState(null);
  const [passkeyBusy, setPasskeyBusy] = useState(false);

  const profileCtx = useContext(ProfileContext) || {};
  const userDetails = useMemo(() => profileCtx.userDetails || {}, [profileCtx.userDetails]);
  const accountEmail = String(userDetails?.emailId || userDetails?.email || "").trim();
  const accountPhoneRaw = String(userDetails?.mobileNumber || userDetails?.phone || "").trim();
  const accountPhoneSplit = useMemo(() => splitInternationalMobile(accountPhoneRaw), [accountPhoneRaw]);
  const accountPhoneDigits = String(accountPhoneRaw || "").replace(/\D/g, "");
  const hasResetEmail = accountEmail.includes("@");
  const hasResetPhone = accountPhoneSplit.phoneDigits.replace(/\D/g, "").length >= 8;
  const showResetTabs = hasResetEmail && hasResetPhone;
  const usernamePart = useMemo(
    () => (accountEmail.includes("@") ? accountEmail.split("@")[0] : accountPhoneDigits),
    [accountEmail, accountPhoneDigits]
  );

  /** Where to send OTP for change-password flow (`securitySendOtp` target); login submit uses matching verify_method. */
  const changePasswordOtpTarget = useMemo(() => {
    if (verifyMethod === "passkey") return null;
    if (verifyMethod === "totp") return null;
    if (verifyMethod === "email") return "email";
    if (verifyMethod === "mobile") return "mobile";
    if (accountEmail.includes("@")) return "email";
    if (accountPhoneDigits.length > 0) return "mobile";
    return null;
  }, [verifyMethod, accountEmail, accountPhoneDigits]);

  // verify_method mapping for AuthService.setSecurity:
  // 1=email, 2=google auth (totp), 3=mobile, 4=passkey
  const changePasswordVerifyMethod = useMemo(() => {
    if (verifyMethod === "passkey") return 4;
    if (verifyMethod === "totp") return 2;
    if (verifyMethod === "mobile") return 3;
    return 1; // email (default)
  }, [verifyMethod]);

  const maskedChangePwdOtpSubtitle = useMemo(() => {
    if (verifyMethod === "passkey") {
      return "Use your passkey (Face ID, Touch ID, or Windows Hello) to verify your identity.";
    }
    if (verifyMethod === "totp") {
      return "Enter the 6-digit code from your authenticator app, valid for 10 minutes.";
    }
    if (accountEmail.includes("@")) {
      const [local, domain] = accountEmail.split("@");
      const vis = Math.min(2, local.length);
      return `The verification code has been sent to ${local.slice(0, vis)}***@${domain}, valid for 10 minutes.`;
    }
    if (accountPhoneDigits.length >= 4) {
      return `The verification code has been sent to your phone ending in ${accountPhoneDigits.slice(-4)}, valid for 10 minutes.`;
    }
    return "The verification code has been sent, valid for 10 minutes.";
  }, [verifyMethod, accountEmail, accountPhoneDigits]);

  const verifyLoginModalTitle = useMemo(() => {
    if (verifyMethod === "passkey") return "Passkey Verification";
    if (verifyMethod === "totp") return "Google Authenticator";
    return changePasswordOtpTarget === "mobile" ? "Verify Your Phone" : "Verify Your Email";
  }, [verifyMethod, changePasswordOtpTarget]);

  const [phase, setPhase] = useState(PHASE.RESTRICT);

  const [otpLogin, setOtpLogin] = useState(["", "", "", "", "", ""]);
  const [otpForgot, setOtpForgot] = useState(["", "", "", "", "", ""]);
  const [authAppCode, setAuthAppCode] = useState("");
  const [isChooseAuthOpen, setIsChooseAuthOpen] = useState(false);

  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");

  const [resetTab, setResetTab] = useState("email");
  const [resetEmail, setResetEmail] = useState("");
  const [resetPhone, setResetPhone] = useState("");
  const [resetCountryCode, setResetCountryCode] = useState("+91");
  const [resetVerificationToken, setResetVerificationToken] = useState("");

  const [newResetPwd, setNewResetPwd] = useState("");
  const [confirmResetPwd, setConfirmResetPwd] = useState("");

  const [showOldPwd, setShowOldPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [showResetNewPwd, setShowResetNewPwd] = useState(false);
  const [showResetConfirmPwd, setShowResetConfirmPwd] = useState(false);

  const [changeSubmitBusy, setChangeSubmitBusy] = useState(false);
  const [changeFormTouched, setChangeFormTouched] = useState(false);
  const [oldTouched, setOldTouched] = useState(false);
  const [newTouched, setNewTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [resetNewTouched, setResetNewTouched] = useState(false);
  const [resetConfirmTouched, setResetConfirmTouched] = useState(false);
  const [otpChannelBusy, setOtpChannelBusy] = useState(false);
  const [otpVerifyBusy, setOtpVerifyBusy] = useState(false);
  const [resetSendBusy, setResetSendBusy] = useState(false);
  const [resetOtpVerifyBusy, setResetOtpVerifyBusy] = useState(false);
  const [resetSubmitBusy, setResetSubmitBusy] = useState(false);

  useEffect(() => {
    if (hasResetEmail) setResetEmail(accountEmail);
    if (hasResetPhone) {
      setResetCountryCode(accountPhoneSplit.countryCode);
      setResetPhone(accountPhoneSplit.phoneDigits);
    }
    if (hasResetEmail && !hasResetPhone) setResetTab("email");
    else if (!hasResetEmail && hasResetPhone) setResetTab("phone");
  }, [accountEmail, accountPhoneSplit.countryCode, accountPhoneSplit.phoneDigits, hasResetEmail, hasResetPhone]);

  const effectiveResetTab = useMemo(() => {
    if (hasResetEmail && !hasResetPhone) return "email";
    if (!hasResetEmail && hasResetPhone) return "phone";
    return resetTab;
  }, [hasResetEmail, hasResetPhone, resetTab]);

  const resetApiType = effectiveResetTab === "email" ? "email" : "mobile";

  const newPwdTrimmed = newPwd.trim();
  const confirmPwdTrimmed = confirmPwd.trim();
  const changePasswordRules = useMemo(() => getPasswordRuleMeta(usernamePart), [usernamePart]);
  const changePasswordRuleRowStates = useMemo(
    () => getPasswordRuleRowStates(newPwdTrimmed, changePasswordRules),
    [newPwdTrimmed, changePasswordRules]
  );
  const newPasswordMeetsAllRules = useMemo(() => {
    const p = String(newPwdTrimmed || "");
    if (!p.trim()) return false;
    return changePasswordRules.every((r) => r.passes(p));
  }, [newPwdTrimmed, changePasswordRules]);
  const changeConfirmEnabled =
    oldPwd.trim().length > 0 &&
    newPasswordMeetsAllRules &&
    newPwdTrimmed === confirmPwdTrimmed &&
    confirmPwdTrimmed.length > 0;

  const resetNextEnabled =
    (effectiveResetTab === "email" ? resetEmail.trim().includes("@") : resetPhone.replace(/\D/g, "").length >= 8) &&
    !resetSendBusy;

  const resetUsernamePart = useMemo(() => {
    if (effectiveResetTab === "email" && resetEmail.trim().includes("@")) {
      return resetEmail.trim().split("@")[0];
    }
    return String(resetPhone || "").replace(/\D/g, "");
  }, [effectiveResetTab, resetEmail, resetPhone]);

  const maskedResetOtpSubtitle = useMemo(() => {
    if (effectiveResetTab === "email" && resetEmail.includes("@")) {
      const [local, domain] = resetEmail.split("@");
      const vis = Math.min(2, local.length);
      return `The verification code has been sent to ${local.slice(0, vis)}***@${domain}, valid for 10 minutes.`;
    }
    const digits = resetPhone.replace(/\D/g, "");
    if (digits.length >= 4) {
      return `The verification code has been sent to your phone ending in ${digits.slice(-4)}, valid for 10 minutes.`;
    }
    return "The verification code has been sent, valid for 10 minutes.";
  }, [effectiveResetTab, resetEmail, resetPhone]);

  const resetVerifyModalTitle = effectiveResetTab === "email" ? "Verify Your Email" : "Verify Your Phone";

  const resetPasswordRules = useMemo(() => getPasswordRuleMeta(resetUsernamePart), [resetUsernamePart]);
  const newResetPwdTrimmed = newResetPwd.trim();
  const confirmResetPwdTrimmed = confirmResetPwd.trim();
  const resetPasswordRuleRowStates = useMemo(
    () => getPasswordRuleRowStates(newResetPwdTrimmed, resetPasswordRules),
    [newResetPwdTrimmed, resetPasswordRules]
  );
  const resetNewPasswordMeetsAllRules = useMemo(() => {
    const p = String(newResetPwdTrimmed || "");
    if (!p.trim()) return false;
    return resetPasswordRules.every((r) => r.passes(p));
  }, [newResetPwdTrimmed, resetPasswordRules]);

  const resetSubmitEnabled = useMemo(() => {
    return (
      resetNewPasswordMeetsAllRules &&
      newResetPwdTrimmed.length > 0 &&
      confirmResetPwdTrimmed.length > 0 &&
      newResetPwdTrimmed === confirmResetPwdTrimmed
    );
  }, [resetNewPasswordMeetsAllRules, newResetPwdTrimmed, confirmResetPwdTrimmed]);

  const goSecurity = () => navigate("/user_profile/security", { replace: true });
  const goSecurityUnavailable = () => navigate("/asset_managemnet/security_verification_unavailable");

  const sendChangePasswordOtp = useCallback(
    async (isResend) => {
      if (verifyMethod === "passkey") return true;
      if (verifyMethod === "totp") return true;
      if (!changePasswordOtpTarget) {
        alertErrorMessage("No email or phone on file to send a verification code.");
        return false;
      }
      const purpose = isFund ? "fund_password" : "change_password";
      setOtpChannelBusy(true);
      try {
        const result = await AuthService.securitySendOtp(changePasswordOtpTarget, purpose);
        if (result?.success) {
          alertSuccessMessage(
            result?.message || (isResend ? "Verification code resent" : "Verification code sent")
          );
          return true;
        }
        alertErrorMessage(result?.message || "Could not send verification code");
        return false;
      } catch (e) {
        alertErrorMessage(e?.message || "Could not send verification code");
        return false;
      } finally {
        setOtpChannelBusy(false);
      }
    },
    [verifyMethod, changePasswordOtpTarget, isFund]
  );

  const pickFallbackVerifyMethod = useCallback(() => {
    const m = resolvedSecurityMethods || {};
    if (m.totp) return "totp";
    if (m.email) return "email";
    if (m.mobile) return "mobile";
    return null;
  }, [resolvedSecurityMethods]);

  const verifyWithPasskey = useCallback(async () => {
    if (passkeyBusy) return false;
    const signId = accountEmail || accountPhoneDigits;
    if (!signId) {
      alertErrorMessage("Account identifier missing for passkey verification.");
      return false;
    }
    setPasskeyBusy(true);
    try {
      const optionsResult = await AuthService.passkeyGetAuthOptions(signId);
      if (!optionsResult?.success || !optionsResult?.data) {
        alertErrorMessage(optionsResult?.message || "Failed to get passkey options");
        return false;
      }
      let credential;
      try {
        credential = await startAuthentication(optionsResult.data);
      } catch (webauthnError) {
        if (webauthnError?.name === "NotAllowedError") {
          // User cancelled / timed out — let caller fall back to another method.
          return "cancelled";
        } else {
          alertErrorMessage("Passkey verification failed.");
        }
        return false;
      }
      const verifyResult = await AuthService.verifyAllSecurityMethods({
        type: "passkey",
        credential,
        code: "",
      });
      if (!verifyResult?.success) {
        alertErrorMessage(verifyResult?.message || "Passkey verification failed");
        return false;
      }
      const uid =
        verifyResult?.data?.userId ||
        userDetails?._id ||
        userDetails?.userId ||
        userDetails?.id ||
        null;
      if (!uid) {
        alertErrorMessage("Could not resolve passkey user id.");
        return false;
      }
      setPasskeyUserId(String(uid));
      return true;
    } catch (e) {
      alertErrorMessage(e?.message || "Passkey verification failed");
      return false;
    } finally {
      setPasskeyBusy(false);
    }
  }, [passkeyBusy, accountEmail, accountPhoneDigits, userDetails]);

  const sendOtpForVerifyMethod = useCallback(async (method) => {
    const purpose = isFund ? "fund_password" : "change_password";
    const target = method === "mobile" ? "mobile" : "email";
    setOtpChannelBusy(true);
    try {
      const result = await AuthService.securitySendOtp(target, purpose);
      if (result?.success) {
        alertSuccessMessage(result?.message || "Verification code sent");
        return true;
      }
      alertErrorMessage(result?.message || "Could not send verification code");
      return false;
    } catch (e) {
      alertErrorMessage(e?.message || "Could not send verification code");
      return false;
    } finally {
      setOtpChannelBusy(false);
    }
  }, [isFund]);

  const priorityOrder = useMemo(() => ["passkey", "totp", "email", "mobile"], []);

  const getAvailableMethodsList = useCallback(() => {
    const m = resolvedSecurityMethods || {};
    return priorityOrder.filter((k) => Boolean(m?.[k]));
  }, [resolvedSecurityMethods, priorityOrder]);

  const openChooseMethod = useCallback(async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getSecurityMethodsList();
      const m =
        result?.data?.security_methods ||
        result?.data?.data?.security_methods ||
        result?.security_methods ||
        result?.data?.securityMethods ||
        null;
      if (!result?.success || !m || typeof m !== "object") {
        alertErrorMessage(result?.message || "Could not load verification methods.");
        return;
      }
      const methodsObj = {
        email: !!m.email,
        mobile: !!(m.mobile ?? m.phone ?? m.sms),
        totp: !!m.totp,
        passkey: !!m.passkey,
      };
      setResolvedSecurityMethods(methodsObj);

      const list = priorityOrder
        .filter((k) => Boolean(methodsObj[k]))
        .filter((k) => k !== verifyMethod);
      if (!list.length) {
        alertErrorMessage("No other verification method available.");
        return;
      }
      setIsChooseAuthOpen(true);
    } catch {
      alertErrorMessage("Could not load verification methods.");
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  }, [priorityOrder, verifyMethod]);

  const pickMethodAndProceed = useCallback(async (method) => {
    setIsChooseAuthOpen(false);
    setPasskeyUserId(null);
    setAuthAppCode("");
    setOtpLogin(["", "", "", "", "", ""]);

    // Passkey: run WebAuthn first; only then set verifyMethod + phase. Avoids VERIFY_LOGIN briefly rendering the wrong modal (OTP) while method was switched to "passkey".
    if (method === "passkey") {
      const verified = await verifyWithPasskey();
      if (verified === true) {
        setVerifyMethod("passkey");
        setPhase(PHASE.CHANGE);
      }
      return;
    }

    setVerifyMethod(method);
    if (method === "totp") {
      setPhase(PHASE.VERIFY_LOGIN);
      return;
    }
    const sent = await sendOtpForVerifyMethod(method);
    if (sent) setPhase(PHASE.VERIFY_LOGIN);
  }, [verifyWithPasskey, sendOtpForVerifyMethod]);

  const handleRestrictConfirm = async () => {
    const ok = await sendChangePasswordOtp(false);
    if (!ok) return;
    if (verifyMethod === "passkey") {
      setPasskeyUserId(null);
      const verified = await verifyWithPasskey();
      if (verified === true) {
        setPhase(PHASE.CHANGE);
        return;
      }
      if (verified === "cancelled") {
        // By default, fall back to next highest-priority method.
        const fallback = pickFallbackVerifyMethod();
        if (!fallback) {
          alertErrorMessage("No supported fallback verification method found.");
          return;
        }
        await pickMethodAndProceed(fallback);
        return;
      }
      return;
    }
    setPhase(PHASE.VERIFY_LOGIN);
  };

  const changeInlineError = useMemo(() => {
    if (!changeFormTouched) return "";
    if (!oldPwd.trim()) return "Please enter your old password.";
    if (!newPwdTrimmed) return "Please enter your new password.";
    const v = validatePasswordAgainstRules(newPwdTrimmed, changePasswordRules);
    if (!v.ok) return v.message;
    if (!confirmPwdTrimmed) return "Please confirm your new password.";
    if (newPwdTrimmed !== confirmPwdTrimmed) return "Confirm password does not match.";
    return "";
  }, [changeFormTouched, oldPwd, newPwdTrimmed, confirmPwdTrimmed, changePasswordRules]);

  const oldFieldError = useMemo(() => {
    if (!(changeFormTouched || oldTouched)) return "";
    if (!oldPwd.trim()) return "Please enter your old password.";
    return "";
  }, [changeFormTouched, oldTouched, oldPwd]);

  const newFieldError = useMemo(() => {
    if (!(changeFormTouched || newTouched)) return "";
    if (!newPwdTrimmed) return "Please enter your new password.";
    const v = validatePasswordAgainstRules(newPwdTrimmed, changePasswordRules);
    if (!v.ok) return v.message;
    return "";
  }, [changeFormTouched, newTouched, newPwdTrimmed, changePasswordRules]);

  const confirmFieldError = useMemo(() => {
    if (!(changeFormTouched || confirmTouched)) return "";
    if (!confirmPwdTrimmed) return "Please confirm your new password.";
    if (newPwdTrimmed && confirmPwdTrimmed && newPwdTrimmed !== confirmPwdTrimmed) return "Confirm password does not match.";
    return "";
  }, [changeFormTouched, confirmTouched, newPwdTrimmed, confirmPwdTrimmed]);

  const resetNewFieldError = useMemo(() => {
    if (!resetNewTouched) return "";
    if (!newResetPwdTrimmed) return "Please enter your new password.";
    const v = validatePasswordAgainstRules(newResetPwdTrimmed, resetPasswordRules);
    if (!v.ok) return v.message;
    return "";
  }, [resetNewTouched, newResetPwdTrimmed, resetPasswordRules]);

  const resetConfirmFieldError = useMemo(() => {
    if (!(resetNewTouched || resetConfirmTouched)) return "";
    if (!confirmResetPwdTrimmed) return "Please confirm your new password.";
    if (
      newResetPwdTrimmed &&
      confirmResetPwdTrimmed &&
      newResetPwdTrimmed !== confirmResetPwdTrimmed
    ) {
      return "Confirm password does not match.";
    }
    return "";
  }, [resetNewTouched, resetConfirmTouched, newResetPwdTrimmed, confirmResetPwdTrimmed]);

  const handleChangePasswordSubmit = async () => {
    setChangeFormTouched(true);
    const err = (() => {
      if (!oldPwd.trim()) return "Please enter your old password.";
      if (!newPwdTrimmed) return "Please enter your new password.";
      const v = validatePasswordAgainstRules(newPwdTrimmed, changePasswordRules);
      if (!v.ok) return v.message;
      if (!confirmPwdTrimmed) return "Please confirm your new password.";
      if (newPwdTrimmed !== confirmPwdTrimmed) return "Confirm password does not match.";
      return "";
    })();
    if (err) {
      alertErrorMessage(err);
      return;
    }
    if (changeSubmitBusy) return;
    setChangeSubmitBusy(true);
    try {
      // Pre-verification (passkey / totp / email / mobile) already done via `verifyAllSecurityMethods`.
      // This call only performs the actual password change with the three fields.
      const res = await AuthService.securityChangeLoginPassword({
        oldPassword: oldPwd,
        newPassword: newPwdTrimmed,
        confirmNewPassword: confirmPwdTrimmed,
      });
      if (res?.success === false) {
        alertErrorMessage(res?.message || "Could not change password");
        return;
      }
      alertSuccessMessage(res?.message || "Password changed successfully");
      goSecurity();
    } catch (e) {
      alertErrorMessage(e?.message || "Could not change password");
    } finally {
      setChangeSubmitBusy(false);
    }
  };

  const verifyLoginCodeBeforeProceed = useCallback(async () => {
    if (otpVerifyBusy) return false;
    const purpose = isFund ? "fund_password" : "change_password";
    const otp = verifyMethod === "totp"
      ? String(authAppCode || "").replace(/\D/g, "").slice(0, 6)
      : otpLogin.join("").replace(/\D/g, "").slice(0, 6);
    if (verifyMethod === "totp") {
      setOtpVerifyBusy(true);
      try {
        const res = await AuthService.verifyAllSecurityMethods({
          type: "totp",
          code: otp,
          credential: "",
        });
        if (res?.success) return true;
        alertErrorMessage(res?.message || "Verification failed");
        return false;
      } catch (e) {
        alertErrorMessage(e?.message || "Verification failed");
        return false;
      } finally {
        setOtpVerifyBusy(false);
      }
    }
    // email/mobile OTP verification
    if (otp.length !== 6) {
      alertErrorMessage("Please enter a valid 6-digit code");
      return false;
    }
    const target = verifyMethod === "mobile" ? "mobile" : "email";
    setOtpVerifyBusy(true);
    try {
      const res = await AuthService.securityVerifyOtp(target, otp, purpose);
      if (res?.success) return true;
      alertErrorMessage(res?.message || "Invalid verification code");
      return false;
    } catch (e) {
      alertErrorMessage(e?.message || "Verification failed");
      return false;
    } finally {
      setOtpVerifyBusy(false);
    }
  }, [otpVerifyBusy, isFund, otpLogin, verifyMethod, authAppCode]);

  const sendResetPasswordOtp = useCallback(
    async (isResend) => {
      setResetSendBusy(true);
      try {
        const result = await AuthService.securityResetPasswordSendOtp(resetApiType);
        if (result?.success) {
          alertSuccessMessage(
            result?.message || (isResend ? "Verification code resent" : "Verification code sent")
          );
          return true;
        }
        alertErrorMessage(result?.message || "Could not send verification code");
        return false;
      } catch (e) {
        alertErrorMessage(e?.message || "Could not send verification code");
        return false;
      } finally {
        setResetSendBusy(false);
      }
    },
    [resetApiType]
  );

  const handleResetPasswordNext = useCallback(async () => {
    if (!resetNextEnabled || resetSendBusy) return;
    const sent = await sendResetPasswordOtp(false);
    if (!sent) return;
    setOtpForgot(["", "", "", "", "", ""]);
    setResetVerificationToken("");
    setPhase(PHASE.VERIFY_FORGOT);
  }, [resetNextEnabled, resetSendBusy, sendResetPasswordOtp]);

  const handleResetPasswordVerifyOtp = useCallback(async () => {
    if (resetOtpVerifyBusy) return false;
    const code = otpForgot.join("").replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      alertErrorMessage("Please enter a valid 6-digit code");
      return false;
    }
    setResetOtpVerifyBusy(true);
    try {
      const res = await AuthService.securityResetPasswordVerifyOtp(resetApiType, code);
      if (!res?.success) {
        alertErrorMessage(res?.message || "Invalid verification code");
        return false;
      }
      const token = res?.data?.verificationToken;
      if (!token) {
        alertErrorMessage("Verification succeeded but no token was returned.");
        return false;
      }
      setResetVerificationToken(String(token));
      setNewResetPwd("");
      setConfirmResetPwd("");
      setResetNewTouched(false);
      setResetConfirmTouched(false);
      setPhase(PHASE.RESET_NEW);
      return true;
    } catch (e) {
      alertErrorMessage(e?.message || "Verification failed");
      return false;
    } finally {
      setResetOtpVerifyBusy(false);
    }
  }, [resetOtpVerifyBusy, otpForgot, resetApiType]);

  const handleResetPasswordSubmit = useCallback(async () => {
    setResetNewTouched(true);
    setResetConfirmTouched(true);
    const v = validatePasswordAgainstRules(newResetPwdTrimmed, resetPasswordRules);
    if (!v.ok) {
      alertErrorMessage(v.message);
      return;
    }
    if (newResetPwdTrimmed !== confirmResetPwdTrimmed) {
      alertErrorMessage("Confirm password does not match.");
      return;
    }
    if (!resetVerificationToken) {
      alertErrorMessage("Verification expired. Please start reset again.");
      setPhase(PHASE.RESET_TABS);
      return;
    }
    if (resetSubmitBusy) return;
    setResetSubmitBusy(true);
    try {
      const res = await AuthService.securityResetPasswordComplete({
        verificationToken: resetVerificationToken,
        newPassword: newResetPwdTrimmed,
        confirmNewPassword: confirmResetPwdTrimmed,
      });
      if (!res?.success) {
        alertErrorMessage(res?.message || "Could not reset password");
        return;
      }
      alertSuccessMessage(res?.message || "Password reset successfully");
      goSecurity();
    } catch (e) {
      alertErrorMessage(e?.message || "Could not reset password");
    } finally {
      setResetSubmitBusy(false);
    }
  }, [
    newResetPwdTrimmed,
    confirmResetPwdTrimmed,
    resetPasswordRules,
    resetVerificationToken,
    resetSubmitBusy,
  ]);

  const openResetPasswordFlow = useCallback(() => {
    if (hasResetEmail) setResetEmail(accountEmail);
    if (hasResetPhone) {
      setResetCountryCode(accountPhoneSplit.countryCode);
      setResetPhone(accountPhoneSplit.phoneDigits);
    }
    if (hasResetEmail && !hasResetPhone) setResetTab("email");
    else if (!hasResetEmail && hasResetPhone) setResetTab("phone");
    setResetVerificationToken("");
    setPhase(PHASE.RESET_TABS);
  }, [hasResetEmail, hasResetPhone, accountEmail, accountPhoneSplit]);

  const renderBreadcrumb = (activeLabel) => (
    <nav className="pm-page__crumbs" aria-label="Breadcrumb">
      <ol className="pm-page__crumbList">
        <li className="pm-page__crumbItem">
          <button type="button" className="pm-page__crumbLink" onClick={goSecurity}>
            Security
          </button>
        </li>
        <li className="pm-page__crumbSep" aria-hidden="true">
          ›
        </li>
        <li className="pm-page__crumbItem pm-page__crumbItem--active" aria-current="page">
          {activeLabel}
        </li>
      </ol>
    </nav>
  );

  return (
    <>
      {phase === PHASE.RESTRICT ? (
        <div className="pm-restrictOverlay" role="presentation">
          <div className="pm-restrictModal" role="dialog" aria-modal="true" aria-labelledby="pm-restrict-title">
            <button type="button" className="pm-restrictModal__close" aria-label="Close" onClick={goSecurity}>
              ×
            </button>
            <div className="pm-restrictModal__art">
              <img className="dark_img" src="/images/security/account_restrictions.svg" alt="" />
              <img className="light_img" src="/images/security/security_risk_vector_light.svg" alt="" />
            </div>
            <h2 id="pm-restrict-title" className="pm-restrictModal__title">
              Account Restrictions
            </h2>
            <p className="pm-restrictModal__text">
              For the security of your account, withdrawals and P2P selling will be temporarily locked for 24 hours after a
              password change.
            </p>
            <div className="pm-restrictModal__actions">
              <button type="button" className="pm-restrictModal__cancel" onClick={goSecurity}>
                Cancel
              </button>
              <button
                type="button"
                className="pm-restrictModal__confirm"
                disabled={
                  otpChannelBusy ||
                  passkeyBusy ||
                  (!["totp", "passkey"].includes(verifyMethod) && !changePasswordOtpTarget)
                }
                onClick={() => void handleRestrictConfirm()}
              >
                {otpChannelBusy ? "Sending…" : (passkeyBusy ? "Verifying…" : "Confirm")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {phase === PHASE.VERIFY_LOGIN && !isChooseAuthOpen ? (
        verifyMethod === "totp" ? (
          <AuthenticatorAppModal
            code={authAppCode}
            setCode={setAuthAppCode}
            busy={otpVerifyBusy}
            onClose={goSecurity}
            onSubmit={async () => {
              const ok = await verifyLoginCodeBeforeProceed();
              if (ok) setPhase(PHASE.CHANGE);
            }}
            onSwitchMethod={openChooseMethod}
            onSecurityUnavailable={goSecurityUnavailable}
          />
        ) : (
          <OtpModal
            title={verifyLoginModalTitle}
            subtitle={maskedChangePwdOtpSubtitle}
            otp={otpLogin}
            setOtp={setOtpLogin}
            onClose={goSecurity}
            onConfirm={async () => {
              const ok = await verifyLoginCodeBeforeProceed();
              if (ok) setPhase(PHASE.CHANGE);
            }}
            onResend={async () => {
              const ok = await sendChangePasswordOtp(true);
              if (ok) setOtpLogin(["", "", "", "", "", ""]);
            }}
            resendDisabled={otpChannelBusy}
            confirmDisabled={otpVerifyBusy}
            confirmLabel={otpVerifyBusy ? "Verifying…" : "Confirm"}
          />
        )
      ) : null}

      {isChooseAuthOpen ? (
        <ChooseAuthenticatorModal
          methods={getAvailableMethodsList().filter((m) => m !== verifyMethod)}
          onClose={() => setIsChooseAuthOpen(false)}
          onPick={pickMethodAndProceed}
          onSecurityUnavailable={goSecurityUnavailable}
        />
      ) : null}

      {phase === PHASE.VERIFY_FORGOT ? (
        <OtpModal
          title={resetVerifyModalTitle}
          subtitle={maskedResetOtpSubtitle}
          otp={otpForgot}
          setOtp={setOtpForgot}
          onClose={() => setPhase(PHASE.RESET_TABS)}
          onConfirm={() => void handleResetPasswordVerifyOtp()}
          onResend={async () => {
            const ok = await sendResetPasswordOtp(true);
            if (ok) setOtpForgot(["", "", "", "", "", ""]);
          }}
          resendDisabled={resetSendBusy}
          confirmDisabled={resetOtpVerifyBusy}
          confirmLabel={resetOtpVerifyBusy ? "Verifying…" : "Confirm"}
        />
      ) : null}

      {phase === PHASE.CHANGE ? (
        <main className="pm-page" aria-labelledby="pm-change-title">
          {renderBreadcrumb(isFund ? "Fund Password" : "Change Login Password")}


          <div className="pm-form">
            <h1 id="pm-change-title" className="pm-page__title pm-page__title--left">
              {isFund ? "Fund Password" : "Change Login Password"}
            </h1>
            <div className="pm-field">
              <label className="pm-label" htmlFor="pm-old">
                Old Password
              </label>
              <div className="pm-inputWrap">
                <input
                  id="pm-old"
                  type={showOldPwd ? "text" : "password"}
                  className={`pm-input pm-input--withToggle${oldFieldError ? " is-error" : ""}`}
                  placeholder="Enter your Old Password"
                  value={oldPwd}
                  onChange={(e) => {
                    setOldPwd(e.target.value);
                    if (!oldTouched) setOldTouched(true);
                  }}
                  onBlur={() => setOldTouched(true)}
                  autoComplete="current-password"
                  aria-invalid={Boolean(oldFieldError)}
                />
                <button
                  type="button"
                  className="pm-togglePw"
                  aria-label={showOldPwd ? "Hide password" : "Show password"}
                  onClick={() => setShowOldPwd((v) => !v)}
                >
                  <i className={showOldPwd ? "ri-eye-line" : "ri-eye-off-line"} aria-hidden="true" />
                </button>
              </div>
              {oldFieldError ? <div className="pm-inlineError" role="alert">{oldFieldError}</div> : null}
            </div>

            <div className="pm-field">
              <label className="pm-label" htmlFor="pm-new">
                New Password
              </label>
              <div className="pm-inputWrap">
                <input
                  id="pm-new"
                  type={showNewPwd ? "text" : "password"}
                  className={`pm-input pm-input--withToggle${newFieldError ? " is-error" : ""}`}
                  placeholder="Enter your New Password"
                  value={newPwd}
                  onChange={(e) => {
                    setNewPwd(e.target.value);
                    if (!newTouched) setNewTouched(true);
                  }}
                  onBlur={() => setNewTouched(true)}
                  autoComplete="new-password"
                  aria-invalid={Boolean(newFieldError)}
                />
                <button
                  type="button"
                  className="pm-togglePw"
                  aria-label={showNewPwd ? "Hide password" : "Show password"}
                  onClick={() => setShowNewPwd((v) => !v)}
                >
                  <i className={showNewPwd ? "ri-eye-line" : "ri-eye-off-line"} aria-hidden="true" />
                </button>
              </div>
              {newFieldError ? <div className="pm-inlineError" role="alert">{newFieldError}</div> : null}
              <div
                className={`signup-wizard-rule-list${newPwdTrimmed ? "" : " signup-wizard-rule-list--empty"}`}
                aria-live="polite"
                aria-label="Password requirements"
              >
                {changePasswordRules.map((rule, idx) => {
                  const st = changePasswordRuleRowStates[idx];
                  const iconClass =
                    st === "ok"
                      ? "ri-checkbox-circle-fill"
                      : st === "bad"
                        ? "ri-close-circle-fill"
                        : st === "pending"
                          ? "ri-indeterminate-circle-line"
                          : "ri-checkbox-blank-circle-line";
                  return (
                    <div key={rule.id} className={`signup-wizard-rule-row signup-wizard-rule-row--${st}`}>
                      <i className={iconClass} aria-hidden />
                      <span>{rule.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="pm-field">
              <label className="pm-label" htmlFor="pm-confirm">
                Confirm Password
              </label>
              <div className="pm-inputWrap">
                <input
                  id="pm-confirm"
                  type={showConfirmPwd ? "text" : "password"}
                  className={`pm-input pm-input--withToggle${confirmFieldError ? " is-error" : ""}`}
                  placeholder="Re-Enter your New Password"
                  value={confirmPwd}
                  onChange={(e) => {
                    setConfirmPwd(e.target.value);
                    if (!confirmTouched) setConfirmTouched(true);
                  }}
                  onBlur={() => setConfirmTouched(true)}
                  autoComplete="new-password"
                  aria-invalid={Boolean(confirmFieldError)}
                />
                <button
                  type="button"
                  className="pm-togglePw"
                  aria-label={showConfirmPwd ? "Hide password" : "Show password"}
                  onClick={() => setShowConfirmPwd((v) => !v)}
                >
                  <i className={showConfirmPwd ? "ri-eye-line" : "ri-eye-off-line"} aria-hidden="true" />
                </button>
              </div>
              {confirmFieldError ? <div className="pm-inlineError" role="alert">{confirmFieldError}</div> : null}
            </div>

            <button
              type="button"
              className={`pm-confirmBtn ${changeConfirmEnabled ? "is-enabled" : ""}`}
              disabled={!changeConfirmEnabled}
              onClick={() => void handleChangePasswordSubmit()}
            >
              {changeSubmitBusy ? "Saving…" : "Confirm"}
            </button>

            {changeInlineError ? (
              <div className="pm-inlineError" role="alert">
                {changeInlineError}
              </div>
            ) : null}

            <button type="button" className="pm-forgot" onClick={openResetPasswordFlow}>
              Forgot password?
            </button>
          </div>
        </main>
      ) : null}

      {phase === PHASE.RESET_TABS ? (
        <main className="pm-page" aria-labelledby="pm-reset-title">
          {renderBreadcrumb("Reset Password")}
          <div className="pm-reset">
            <h1 id="pm-reset-title" className="pm-reset__title">
              Reset Your Password
            </h1>

            {showResetTabs ? (
              <div className="pm-tabs">
                <button
                  type="button"
                  className={`pm-tab ${effectiveResetTab === "email" ? "is-active" : ""}`}
                  onClick={() => setResetTab("email")}
                >
                  Email
                </button>
                <button
                  type="button"
                  className={`pm-tab ${effectiveResetTab === "phone" ? "is-active" : ""}`}
                  onClick={() => setResetTab("phone")}
                >
                  Phone
                </button>
              </div>
            ) : null}

            {effectiveResetTab === "email" ? (
              <div className="pm-field">
                <EmailInputWithDomainSuggest
                  inputClassName="pm-input"
                  wrapClassName="eids-email-wrap"
                  placeholder="Enter email address"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  autoComplete="email"
                  readOnly
                />
              </div>
            ) : (
              <div className="pm-phoneWrap">
                <AuthPhoneField
                  countryCode={resetCountryCode}
                  onCountryChange={setResetCountryCode}
                  phone={resetPhone}
                  onPhoneChange={(digits) => setResetPhone(digits.slice(0, 15))}
                  inputId="pm-reset-country-code"
                  phonePlaceholder="Enter phone number"
                  readOnly
                  selectProps={{ isDisabled: true }}
                />
              </div>
            )}

            <button
              type="button"
              className="pm-nextBtn"
              disabled={!resetNextEnabled}
              onClick={() => void handleResetPasswordNext()}
            >
              {resetSendBusy ? "Sending…" : "Next"}
            </button>
          </div>
        </main>
      ) : null}

      {phase === PHASE.RESET_NEW ? (
        <main className="pm-page" aria-labelledby="pm-resetnew-title">
          {renderBreadcrumb("Reset Password")}
          <div className="pm-resetNew">
            <h1 id="pm-resetnew-title" className="pm-resetNew__title">
              Reset Your Password
            </h1>

            <div className="pm-field">
              <label className="pm-label" htmlFor="pm-newp">
                New Password
              </label>
              <div className="pm-inputWrap">
                <input
                  id="pm-newp"
                  type={showResetNewPwd ? "text" : "password"}
                  className={`pm-input pm-input--withToggle${resetNewFieldError ? " is-error" : ""}`}
                  placeholder="Enter New Password"
                  value={newResetPwd}
                  onChange={(e) => {
                    setNewResetPwd(e.target.value);
                    if (!resetNewTouched) setResetNewTouched(true);
                  }}
                  onBlur={() => setResetNewTouched(true)}
                  autoComplete="new-password"
                  aria-invalid={Boolean(resetNewFieldError)}
                />
                <button
                  type="button"
                  className="pm-togglePw"
                  aria-label={showResetNewPwd ? "Hide password" : "Show password"}
                  onClick={() => setShowResetNewPwd((v) => !v)}
                >
                  <i className={showResetNewPwd ? "ri-eye-line" : "ri-eye-off-line"} aria-hidden="true" />
                </button>
              </div>
              {resetNewFieldError ? <div className="pm-inlineError" role="alert">{resetNewFieldError}</div> : null}
              <div
                className={`signup-wizard-rule-list${newResetPwdTrimmed ? "" : " signup-wizard-rule-list--empty"}`}
                aria-live="polite"
                aria-label="Password requirements"
              >
                {resetPasswordRules.map((rule, idx) => {
                  const st = resetPasswordRuleRowStates[idx];
                  const iconClass =
                    st === "ok"
                      ? "ri-checkbox-circle-fill"
                      : st === "bad"
                        ? "ri-close-circle-fill"
                        : st === "pending"
                          ? "ri-indeterminate-circle-line"
                          : "ri-checkbox-blank-circle-line";
                  return (
                    <div key={rule.id} className={`signup-wizard-rule-row signup-wizard-rule-row--${st}`}>
                      <i className={iconClass} aria-hidden />
                      <span>{rule.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="pm-field">
              <label className="pm-label" htmlFor="pm-newp2">
                Enter Password Again
              </label>
              <div className="pm-inputWrap">
                <input
                  id="pm-newp2"
                  type={showResetConfirmPwd ? "text" : "password"}
                  className={`pm-input pm-input--withToggle${resetConfirmFieldError ? " is-error" : ""}`}
                  placeholder="Confirm Password"
                  value={confirmResetPwd}
                  onChange={(e) => {
                    setConfirmResetPwd(e.target.value);
                    if (!resetConfirmTouched) setResetConfirmTouched(true);
                  }}
                  onBlur={() => setResetConfirmTouched(true)}
                  autoComplete="new-password"
                  aria-invalid={Boolean(resetConfirmFieldError)}
                />
                <button
                  type="button"
                  className="pm-togglePw"
                  aria-label={showResetConfirmPwd ? "Hide password" : "Show password"}
                  onClick={() => setShowResetConfirmPwd((v) => !v)}
                >
                  <i className={showResetConfirmPwd ? "ri-eye-line" : "ri-eye-off-line"} aria-hidden="true" />
                </button>
              </div>
              {resetConfirmFieldError ? <div className="pm-inlineError" role="alert">{resetConfirmFieldError}</div> : null}
            </div>

            <button
              type="button"
              className={`pm-resetBtn ${resetSubmitEnabled ? "is-enabled" : ""}`}
              disabled={!resetSubmitEnabled || resetSubmitBusy}
              onClick={() => void handleResetPasswordSubmit()}
            >
              {resetSubmitBusy ? "Saving…" : "Reset"}
            </button>
          </div>
        </main>
      ) : null}
    </>
  );
};

export default ChangeLoginPassword;
